import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AudioPlayer from "react-h5-audio-player";
import "react-h5-audio-player/lib/styles.css";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
  TimelineOppositeContent,
} from "@mui/lab";
import { useDispatch, useSelector } from "react-redux";
import { getPlaylistRecordingPlaybackUrl } from "../../../../store/Playlists/playlistsThunks";
import {
  fetchRecordedCallReviewForm,
  saveRecordedCallReviewForm,
} from "../../../../store/RecordedCalls/recordedCallsThunks";
import NRSpokenCommsReviewForm from "./NRSpokenCommsReviewForm";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const PlayPanelContentWithComments = ({ row, playlistId, onClose }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [audioUrl, setAudioUrl] = useState(null);
  const [comments, setComments] = useState([]);
  const [newTime, setNewTime] = useState("");
  const [newComment, setNewComment] = useState("");
  const [activeView, setActiveView] = useState("review");
  const [reviewPayload, setReviewPayload] = useState(null);
  const [payloadCopied, setPayloadCopied] = useState(false);

  const {
    loadingPlayback,
    playbackError,
    reviewFormsByKey,
    reviewFormLoading,
    reviewFormSaving,
    reviewFormError,
  } = useSelector((state) => state.recordedCalls);

  const reviewKey = useMemo(
    () => `${playlistId}:${row.id}`,
    [playlistId, row.id]
  );
  const reviewFormData = reviewFormsByKey?.[reviewKey] || null;

  useEffect(() => {
    let currentObjectUrl = null;

    const fetchAudio = async () => {
      try {
        const action = await dispatch(
          getPlaylistRecordingPlaybackUrl({
            recordingId: row.id,
            playlistId: playlistId,
          })
        );

        if (action.meta.requestStatus === "fulfilled") {
          const blob = new Blob([action.payload], { type: "audio/mpeg" });
          currentObjectUrl = URL.createObjectURL(blob);
          setAudioUrl(currentObjectUrl);
        } else {
          console.error("Playback error:", action.payload);
        }
      } catch (error) {
        console.error("Error dispatching playback thunk", error);
      }
    };

    fetchAudio();

    return () => {
      if (currentObjectUrl) {
        URL.revokeObjectURL(currentObjectUrl);
      }
    };
  }, [dispatch, playlistId, row.id]);

  useEffect(() => {
    dispatch(
      fetchRecordedCallReviewForm({
        playlistId,
        recordedCallId: row.id,
      })
    );
  }, [dispatch, playlistId, row.id]);

  const handleSubmitComment = () => {
    if (!newTime || !newComment) return;
    setComments((prev) => [
      ...prev,
      { time: newTime, text: newComment, id: Date.now() },
    ]);
    setNewTime("");
    setNewComment("");
  };

  const handleToggleView = useCallback((_, value) => {
    if (value) {
      setActiveView(value);
    }
  }, []);

  const handlePayloadChange = useCallback((payload) => {
    setReviewPayload(payload);
    setPayloadCopied(false);
  }, []);

  const handleCopyPayload = useCallback(async () => {
    if (!reviewPayload) return;
    try {
      await navigator.clipboard.writeText(
        JSON.stringify(reviewPayload, null, 2)
      );
      setPayloadCopied(true);
    } catch (error) {
      console.error("Failed to copy review payload", error);
    }
  }, [reviewPayload]);

  const handleSaveReview = useCallback(() => {
    if (!reviewPayload) return;
    dispatch(
      saveRecordedCallReviewForm({
        playlistId,
        recordedCallId: row.id,
        payload: reviewPayload,
      })
    );
  }, [dispatch, playlistId, row.id, reviewPayload]);

  const reviewStatusChip = useMemo(() => {
    if (!reviewPayload) {
      return null;
    }

    return reviewPayload.completed ? (
      <Chip label="All questions answered" color="success" size="small" />
    ) : (
      <Chip
        label="Some questions pending"
        color="warning"
        size="small"
      />
    );
  }, [reviewPayload]);

  return (
    <Box sx={{ p: 2, position: "relative" }}>
      <IconButton
        onClick={onClose}
        size="small"
        sx={{ position: "absolute", top: 8, right: 8 }}
      >
        <CloseIcon />
      </IconButton>

      {loadingPlayback && <p>Loading audio...</p>}
      {playbackError && <p style={{ color: "red" }}>{playbackError}</p>}
      {audioUrl && (
        <Box
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: theme.shadows[4],
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <AudioPlayer
            src={audioUrl}
            showJumpControls={false}
            autoPlay={false}
            layout="horizontal-reverse"
          />
        </Box>
      )}

      <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup
            value={activeView}
            exclusive
            onChange={handleToggleView}
            size="small"
          >
            <ToggleButton value="review">Review Form</ToggleButton>
            <ToggleButton value="comments">Timestamped Comments</ToggleButton>
          </ToggleButtonGroup>
          {reviewStatusChip}
        </Stack>

        {activeView === "review" && (
          <Stack spacing={2}>
            <NRSpokenCommsReviewForm
              playlistId={playlistId}
              recordedCallId={row.id}
              onPayloadChange={handlePayloadChange}
              initialPayload={reviewFormData}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button
                variant="contained"
                onClick={handleSaveReview}
                disabled={!reviewPayload || reviewFormSaving}
              >
                {reviewFormSaving ? "Saving..." : "Save review"}
              </Button>
              {reviewFormLoading && (
                <Chip label="Loading saved review..." size="small" />
              )}
              {reviewFormError && (
                <Chip label="Failed to load review" color="error" size="small" />
              )}
            </Stack>
          </Stack>
        )}

        {activeView === "comments" && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {comments.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    minHeight: 150,
                  }}
                >
                  No comments
                </Box>
              ) : (
                <Timeline position="alternate">
                  {comments.map((comment) => (
                    <TimelineItem key={comment.id}>
                      <TimelineOppositeContent color="text.secondary">
                        {comment.time}
                      </TimelineOppositeContent>
                      <TimelineSeparator>
                        <TimelineDot />
                        <TimelineConnector />
                      </TimelineSeparator>
                      <TimelineContent>{comment.text}</TimelineContent>
                    </TimelineItem>
                  ))}
                </Timeline>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Time"
                value={newTime}
                onChange={(e) => setNewTime(e.target.value)}
                fullWidth
                sx={{ mb: 1 }}
              />
              <TextField
                label="Comment"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                fullWidth
                multiline
                minRows={3}
                sx={{ mb: 1 }}
              />
              <Button variant="contained" onClick={handleSubmitComment} fullWidth>
                Add Comment
              </Button>
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default PlayPanelContentWithComments;
